import { useEffect, useState } from 'react';
import { apiClient } from '@/services/api';

/**
 * The receipt image behind a ledger row, as an object URL.
 *
 * `GET /documents/:id/file` carries the family check and the PHI audit, and
 * the access token lives in memory (never a cookie), so a plain `<img src>`
 * could not authenticate. Fetch as a blob through the API client and hand the
 * row an object URL; revoke it when the row unmounts or the id changes.
 *
 * Only raster receipts are fetched — a PDF shows a document glyph instead.
 */
export function useReceiptThumbnail(sourceDocumentId: string | null, mimeType: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const isImage = Boolean(mimeType && mimeType.startsWith('image/'));

  useEffect(() => {
    if (!sourceDocumentId || !isImage || typeof URL.createObjectURL !== 'function') { setUrl(null); return; }
    let cancelled = false;
    let objectUrl: string | null = null;
    apiClient
      .get<Blob>(`/documents/${sourceDocumentId}/file`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setUrl(null); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [sourceDocumentId, isImage]);

  return url;
}
